import React, { useState, useEffect } from "react";
import { Button, Popconfirm, message } from "antd";
import { CloseOutlined, ShopOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  clearImpersonationSession,
  setImpersonationSession
} from "../slices/impersonationSlice";
import {
  _end_impersonation_session,
  _get_active_impersonation_sessions
} from "../common/axios_services";

const GlobalImpersonationBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const impersonation = useSelector((state) => state.impersonation);
  const user = useSelector((state) => state.userProfile.user);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const recoverSession = async () => {
      const supportRoles = ["ADMIN", "SUPPORT_OFFICER", "SUPPORT_MANAGER", "SUPPORT_EXECUTIVE"];

      if (user?.role && supportRoles.includes(user.role) && !impersonation?.isImpersonating) {
        try {
          const response = await _get_active_impersonation_sessions();
          const activeSessions = response.data?.active || [];

          if (activeSessions.length > 0) {
            const session = activeSessions[0];
            dispatch(setImpersonationSession({
              sessionId: session.id,
              businessId: session.businessId,
              businessName: session.businessName,
              token: null, // JWT isn't recoverable from backend, but banner can show status
              tokenPreview: session.tokenPreview,
              expiresAt: session.expiresAt
            }));
          }
        } catch (error) {
          console.error("Failed to recover active impersonation session:", error);
        }
      }
    };

    recoverSession();
  }, [user?.id, impersonation?.isImpersonating, dispatch]);

  useEffect(() => {
    if (impersonation?.isImpersonating) {
      document.body.style.paddingTop = "42px";
    } else {
      document.body.style.paddingTop = "0";
    }

    return () => {
      document.body.style.paddingTop = "0";
    };
  }, [impersonation?.isImpersonating]);

  if (!impersonation?.isImpersonating) {
    return null;
  }

  const handleEndSession = async () => {
    try {
      setLoading(true);
      await _end_impersonation_session(impersonation.sessionId);
      dispatch(clearImpersonationSession());
      message.success("Impersonation session ended");
      navigate("/support/businesses");
    } catch (error) {
      console.error("Failed to end session:", error);
      message.error("Failed to end session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: "#ff4d4f",
        color: "white",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        fontWeight: 600,
        fontSize: "14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <ShopOutlined style={{ fontSize: "16px" }} />
        <span>You're managing {impersonation.businessName}</span>
      </div>

      <Popconfirm
        title="End Impersonation Session?"
        description="You will be logged out and returned to the support dashboard."
        onConfirm={handleEndSession}
        okText="Yes, End Session"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <Button
          type="ghost"
          size="small"
          style={{
            color: "white",
            borderColor: "white",
          }}
          icon={<CloseOutlined />}
          loading={loading}
        >
          End Session
        </Button>
      </Popconfirm>
    </div>
  );
};

export default GlobalImpersonationBanner;
